import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, Check, CheckCircle2, Clock, ListTodo, X as XIcon, MessageSquareText, Camera, Circle, ClipboardCheck, RefreshCw, Play, Flag } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { notifications as notifApi } from "../lib/api";
import { localizedCategory } from "../lib/i18nContent";

const KIND_META = {
  finding_started:      { icon: Clock,             tone: "text-amber-600 bg-amber-50",   label_key: "notifications.kind_started" },
  finding_done:         { icon: CheckCircle2,      tone: "text-emerald-600 bg-emerald-50", label_key: "notifications.kind_done" },
  finding_cannot_do:    { icon: XIcon,             tone: "text-red-600 bg-red-50",       label_key: "notifications.kind_cannot_do" },
  finding_reopened:     { icon: ListTodo,          tone: "text-stone-700 bg-stone-100",  label_key: "notifications.kind_reopened" },
  finding_note_updated: { icon: MessageSquareText, tone: "text-blue-600 bg-blue-50",     label_key: "notifications.kind_note" },
  finding_photo_added:  { icon: Camera,            tone: "text-fuchsia-600 bg-fuchsia-50", label_key: "notifications.kind_photo" },
  assignment_assigned:    { icon: ClipboardCheck,    tone: "text-blue-700 bg-blue-50",       label_key: "notifications.kind_assignment_assigned" },
  assignment_reassigned:  { icon: RefreshCw,         tone: "text-indigo-600 bg-indigo-50",   label_key: "notifications.kind_assignment_reassigned" },
  assignment_started:     { icon: Play,              tone: "text-amber-600 bg-amber-50",     label_key: "notifications.kind_assignment_started" },
  assignment_completed:   { icon: CheckCircle2,      tone: "text-emerald-600 bg-emerald-50", label_key: "notifications.kind_assignment_completed" },
  assignment_comment:     { icon: MessageSquareText, tone: "text-sky-600 bg-sky-50",         label_key: "notifications.kind_assignment_comment" },
  audit_flag:             { icon: Flag,              tone: "text-red-600 bg-red-50",         label_key: "notifications.kind_audit_flag" },
};

/**
 * Bell dropdown for the top-right nav bar. Polls unread notifications every
 * 30 seconds and offers:
 *  - live unread badge count
 *  - list of the last 30 notifications with quick "mark all read"
 *  - click a row -> mark read + jump to the finding on the /findings page
 */
export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const resp = await notifApi.list({ limit: 30 });
      const rows = Array.isArray(resp) ? resp : (resp?.items || []);
      setItems(rows.slice(0, 30));
    } catch {
      // silent; the bell is best-effort
    }
  };

  // Initial load + poll every 30s. Also re-load when the dropdown opens
  // so the user sees fresh data without having to wait for the timer.
  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open]);

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

  const markAllRead = async () => {
    try {
      await notifApi.markAllRead();
      setItems((rows) => rows.map((n) => ({ ...n, read: true })));
    } catch {
      /* noop */
    }
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await notifApi.markRead(n.id); } catch { /* noop */ }
      setItems((rows) => rows.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
    }
    // Jump to the finding on the /findings page (opens the row automatically
    // if we pass ?highlight=<finding_id>). Also handled by FindingsPage.
    if (n.assignment_id) {
      setOpen(false);
      navigate(`/assignments/${n.assignment_id}`);
    } else if (n.finding_id) {
      setOpen(false);
      navigate(`/findings?highlight=${n.finding_id}`);
    } else if (n.audit_id) {
      setOpen(false);
      navigate(`/summary/${n.audit_id}`);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-stone-100"
          data-testid="notification-bell-trigger"
          title={t("notifications.title", "Notifications")}
        >
          <Bell size={16} />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center leading-none"
              data-testid="notification-bell-badge"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] max-h-[500px] overflow-hidden p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-stone-200 bg-white">
          <div className="text-sm font-semibold text-stone-800 flex items-center gap-1.5">
            <Bell size={14} /> {t("notifications.title", "Notifications")}
            {unreadCount > 0 && (
              <span className="text-[11px] text-red-600 font-bold ml-1">
                {t("notifications.unread_n", "{{n}} unread", { n: unreadCount })}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-[11px] font-medium text-[#1E3A8A] hover:underline"
              data-testid="notification-mark-all-read"
            >
              {t("notifications.mark_all_read", "Marcar todo leído")}
            </button>
          )}
        </div>

        {/* List */}
        <div className="max-h-[440px] overflow-y-auto" data-testid="notification-list">
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-stone-500">
              {t("notifications.empty", "No hay notificaciones todavía.")}
            </div>
          )}
          {items.map((n) => {
            const meta = KIND_META[n.kind] || {
              icon: Circle,
              tone: "text-stone-500 bg-stone-100",
              label_key: null,
            };
            const Icon = meta.icon;
            const when = (n.created_at || "").slice(0, 16).replace("T", " ");
            const kindLabel = meta.label_key ? t(meta.label_key) : n.kind;
            return (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left flex items-start gap-2.5 px-3 py-2.5 border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors ${
                  n.read ? "" : "bg-blue-50/40"
                }`}
                data-testid={`notification-item-${n.id}`}
              >
                <span className={`inline-flex w-7 h-7 rounded-full ${meta.tone} items-center justify-center shrink-0`}>
                  <Icon size={13} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[11px] text-stone-500">
                    <span className="font-semibold text-stone-800 truncate">{kindLabel}</span>
                    {!n.read && <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />}
                    <span className="ml-auto text-[10.5px] text-stone-400 tabular-nums shrink-0">{when}</span>
                  </div>
                  <div className="text-[12.5px] text-stone-700 mt-0.5 line-clamp-2">
                    <span className="font-medium">{n.store_name}</span>
                    {n.category && <> · {localizedCategory(n.category, i18n.language)}</>}
                    {n.question && <> — {n.question.slice(0, 80)}</>}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    {t("notifications.by", "por")} <span className="font-medium">{n.sender_name || "Anonymous"}</span>
                    {n.from_status && n.to_status && (
                      <> · <span className="font-mono text-[10px]">{n.from_status} → {n.to_status}</span></>
                    )}
                    {n.note_preview && <> · &ldquo;{n.note_preview}&rdquo;</>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-stone-200 bg-stone-50 px-3 py-2 flex items-center justify-between text-[11px] text-stone-500">
            <span>{t("notifications.showing_n", "{{n}} mostradas", { n: items.length })}</span>
            <button
              onClick={() => { setOpen(false); navigate("/notifications"); }}
              className="font-medium text-[#1E3A8A] hover:underline inline-flex items-center gap-1"
              data-testid="notification-view-all"
            >
              <Check size={11} /> {t("notifications.view_all", "Ver todo")}
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
